import React, { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ForensicOverlay from "./ForensicOverlay";

/**
 * AuthImage – henter signert korttidsholdbar URL (60s TTL) fra
 * /api/media/sign og bruker den som src. Signaturen dør raskt, så
 * hvis noen kopierer URL-en fra DevTools kan de ikke dele den videre.
 *
 * FORENSISK VANNMERKE: Når `imageId` er satt (dvs. bildet er brukergenerert
 * innhold, ikke en ekstern avatar-URL), legges automatisk et usynlig
 * forensisk vannmerke over bildet. Kan skrus av med `noTrack`-prop for
 * UI-elementer der sporing ikke gir mening.
 *
 * OWNER-DIAGNOSE: Hvis den innloggede brukeren har `role="owner"` og
 * bildet feiler å laste, rendres en synlig overlay med feilstatus
 * (sign-fetch status + image-fetch status + image-id) i stedet for
 * kun en svart boks. Vanlige brukere ser fortsatt bare bakgrunnen.
 */
export default function AuthImage({
  imageId,
  alt = "",
  className = "",
  fallback = null,
  allowGuest = false,
  noTrack = false,
  ...imgProps
}) {
  const [src, setSrc] = useState(null);
  const [signStatus, setSignStatus] = useState(null);
  const [lockCode, setLockCode] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const timer = useRef(null);
  const authCtx = useAuth();
  const isOwner = authCtx?.user?.role === "owner";

  // Owner-diag i bunn-strip, ikke full-screen rød overlay
  const ownerDiag = (reason) => (
    <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/70 text-[8px] text-red-300/90 font-mono px-1 py-0.5 leading-tight break-all pointer-events-none">
      ⚠ {reason} · sign:{signStatus ?? "…"} · id:{String(imageId).slice(0, 8)}
    </div>
  );

  // Auto-retry ved img-error (upload race): filen kan være i storage-write når img prøver å laste
  useEffect(() => {
    if (!imgError || retryCount >= 3 || !imageId) return;
    const delay = 700 * (retryCount + 1);
    const t = setTimeout(() => {
      setImgError(false);
      setRetryCount((c) => c + 1);
      setSrc(null);
      setSignStatus(null);
    }, delay);
    return () => clearTimeout(t);
  }, [imgError, retryCount, imageId]);

  useEffect(() => {
    if (!imageId) { setSrc(null); setSignStatus(null); setImgError(false); return; }
    let cancelled = false;
    setImgError(false);

    const refresh = async () => {
      try {
        // Bruk axios `api` slik at Bearer-token fra localStorage legges på
        // som fallback når cookies mangler (in-app-browsere).
        const r = await api.get(`/media/sign`, { params: { kind: "image", id: imageId } });
        if (!cancelled) setSignStatus(r.status);
        if (r.status !== 200) {
          if (!cancelled) setSrc(null);
          return;
        }
        const j = r.data;
        if (cancelled) return;
        setSrc(`${API.replace(/\/api$/, "")}${j.url}`);
        clearTimeout(timer.current);
        timer.current = setTimeout(refresh, 55_000);
      } catch (e) {
        const st = e?.response?.status || -1;
        if (!cancelled) { setSrc(null); setSignStatus(st); setLockCode(e?.response?.data?.detail?.code || null); }
      }
    };
    refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [imageId, retryCount]);

  if (!imageId) return fallback || <div className={`bg-zinc-900 ${className}`} />;
  const guestClass = allowGuest ? "no-guest-blur" : "guest-blur-target";

  if (!src && lockCode === "locked_vip_video") {
    return (<div className={`${className} ${guestClass} bg-zinc-900 relative flex flex-col items-center justify-center gap-1`} data-testid="media-locked-vip-video">
        <span className="px-2 py-0.5 bg-[var(--gold)] text-[var(--noir)] text-[10px] font-bold uppercase tracking-wider">VIP</span>
        <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream)]">Film krever VIP</span>
      </div>);
  }
  // Ingen src (sign feilet eller pågår)
  if (!src) {
    const failed = signStatus !== null && signStatus !== 200 && signStatus !== -0;
    return (
      <div className={`${className} ${guestClass} bg-zinc-900 relative`}>
        {isOwner && failed && ownerDiag(`SIGN ${signStatus}`)}
      </div>
    );
  }

  // src finnes, men <img> feilet (typisk 401/404 fra /raw)
  if (imgError) {
    return (
      <div className={`${className} ${guestClass} bg-zinc-900 relative`}>
        {isOwner && ownerDiag("IMG LOAD FAIL")}
      </div>
    );
  }

  // Forensisk vannmerke aktivt kun for brukergenerert innhold (imageId satt)
  // og ikke eksplisitt slått av. Wrapper i span så overlay kan posisjonere
  // seg absolutt uten å bryte eksisterende layout.
  const shouldTrack = !!imageId && !noTrack;
  const imgEl = (
    <img
      src={src}
      alt={alt}
      className={`${className} ${guestClass}`}
      loading="lazy"
      onError={() => setImgError(true)}
      draggable={false}
      {...imgProps}
    />
  );
  if (!shouldTrack) return imgEl;
  // Kun wrap i sized span når kalleren har `w-full`/`h-full` i className
  // (utforsk-kort etc). Ellers bevar historisk `inline-block`-oppførsel
  // (lightbox/modal etc) så natural intrinsic image-størrelse bestemmer.
  const wrapperNeedsSizing = /(^|\s)(w-full|h-full)(\s|$)/.test(className);
  const wrapperClass = wrapperNeedsSizing
    ? `relative block w-full h-full ${className}`
    : "relative inline-block";
  return (
    <span className={wrapperClass} style={{ lineHeight: 0 }}>
      {imgEl}
      <ForensicOverlay viewId={imageId} testId="thumb-forensic" />
    </span>
  );
}
